/**
 * columnBalance.js
 *
 * Splits an ordered list of card heights into balanced columns.
 *
 * CSS multi-column was tried first and is not enough: the browser fills
 * greedily against a target height it computes itself, so on the settings page
 * it kept the first column half empty while the third one ran a full screen
 * longer. What is wanted is the split that minimises the tallest column, which
 * is a decision on measured heights, not a hint the layout engine may follow.
 *
 * The split keeps the cards in order and gives each column a contiguous run:
 * reading order is the order they were written in, the same promise CSS
 * columns make. Only where the cuts fall is chosen here.
 *
 * Copyright (c) 2026 Eric Seigne <eric.seigne@cap-rel.fr>
 *
 * This program is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as
 * published by the Free Software Foundation, either version 3 of the
 * License, or (at your option) any later version.
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with this program.  If not, see <https://www.gnu.org/licenses/>.
 */

// Column each card belongs to, as an array of indices parallel to `heights`.
// An unusable height or column count falls back to a single column: a settings
// page stacked in one column is readable, one laid out on NaN is not.
export function balancedPartition(heights, columnCount) {
  if (!Array.isArray(heights)) {
    console.error('balancedPartition: heights is not an array, falling back to a single column', heights)
    return []
  }

  const usable = heights.every(h => Number.isFinite(h) && h >= 0)
  if (!usable) {
    console.error('balancedPartition: a height is not a usable number, falling back to a single column', heights)
    return heights.map(() => 0)
  }

  if (!Number.isInteger(columnCount) || columnCount < 1) {
    console.error(`balancedPartition: ${columnCount} is not a usable column count, falling back to a single column`)
    return heights.map(() => 0)
  }

  const n = heights.length
  if (n === 0) return []
  if (columnCount === 1) return heights.map(() => 0)

  // prefix[i] is the height of the first i cards, so the run [j, i) costs
  // prefix[i] - prefix[j]
  const prefix = Array.from({ length: n + 1 }, () => 0)
  for (let i = 0; i < n; i++) prefix[i + 1] = prefix[i] + heights[i]

  // best[k][i]: tallest column of the best split of the first i cards into k
  // columns. cut[k][i]: where the k-th column starts in that split.
  const best = [null, prefix.slice()]
  const cut = [null, Array.from({ length: n + 1 }, () => 0)]

  for (let k = 2; k <= columnCount; k++) {
    const row = Array.from({ length: n + 1 }, () => Infinity)
    const cuts = Array.from({ length: n + 1 }, () => 0)
    for (let i = 0; i <= n; i++) {
      for (let j = 0; j <= i; j++) {
        // An empty run is allowed: with fewer cards than columns, or with one
        // card taller than everything else, leaving a column empty is the
        // best split there is.
        const candidate = Math.max(best[k - 1][j], prefix[i] - prefix[j])
        // On a tie, keep the largest cut, which makes the last column the
        // lightest of the equally good splits. Preferring the smallest one
        // filled the page from the right and left the first column empty
        // whenever there were fewer cards than columns.
        if (candidate <= row[i]) {
          row[i] = candidate
          cuts[i] = j
        }
      }
    }
    best[k] = row
    cut[k] = cuts
  }

  // Walk the cuts back from the last column to the first
  const assignment = Array.from({ length: n }, () => 0)
  let end = n
  for (let k = columnCount; k >= 1; k--) {
    const start = k === 1 ? 0 : cut[k][end]
    for (let i = start; i < end; i++) assignment[i] = k - 1
    end = start
  }

  return assignment
}
